/**
 * Hook for managing collapsible tree state with localStorage persistence.
 */
import { useState, useEffect, useCallback, useRef } from "react";

export function useCollapsedState(storageKey: string) {
  const hasStoredValue = useRef(localStorage.getItem(storageKey) !== null);

  const [collapsedKeys, setCollapsedKeys] = useState<Set<string>>(() => {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        return new Set(JSON.parse(stored));
      } catch {
        /* ignore */
      }
    }
    return new Set();
  });

  // Persist to localStorage on change
  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify([...collapsedKeys]));
  }, [storageKey, collapsedKeys]);

  const toggleCollapsed = useCallback((key: string) => {
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  /**
   * Initialize default collapsed state (all parents collapsed).
   * Only applies on first load when no localStorage value exists.
   */
  const initDefaults = useCallback((parentKeys: Set<string>) => {
    if (!hasStoredValue.current && parentKeys.size > 0) {
      setCollapsedKeys(parentKeys);
      hasStoredValue.current = true;
    }
  }, []);

  return { collapsedKeys, toggleCollapsed, initDefaults } as const;
}
